import { useCallback, useContext, useEffect, useState } from 'react'
import { Outlet, createFileRoute, redirect, useNavigate, useParams, useRouterState } from '@tanstack/react-router'
import { useHotkeys } from 'react-hotkeys-hook'
import type {CSSProperties, ReactNode} from 'react';
import { TaskRowCountsProvider } from '@/routes/features/tasks/components/TaskRowCountsProvider'
import { AppLayoutContext } from '@/context/AppLayoutContext'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { PhoneMenuButton } from '@/components/app/layout/PhoneMenuButton'
import { AppSidebar } from '@/routes/layout/app-layout/components/AppSidebar'
import { AppContext } from '@/context'
import { Toaster } from '@/components/ui/sonner'
import { Loading } from '@/components/app/indicators/Loading'
import { isAuthenticated } from '@/lib/auth/authSession'
import getWorkspaces from '@/lib/api/features/authentication/api/getWorkspaces'
import { TrialBanner } from '@/components/app/subscription/TrialBanner'
import { PWAUpdatePrompt } from '@/components/features/pwa/PWAUpdatePrompt'
import { GO_TO_PLACES, SearchDialog } from '@/components/app/search'
import { AskWorkspaceDialog } from '@/components/app/ai/AskWorkspaceDialog'
import { QuickCreateTaskDialog } from '@/components/app/QuickCreateTaskDialog'
import { useRouteProjectId } from '@/hooks/useRouteProjectId'
import useLoginStore from '@/store/LoginStore'
import {
  getSidebarViewType,
  resolveSidebarMode,
  useSidebarModeStore,
} from '@/store/SidebarModeStore'
import { useIsNarrowDesktop } from '@/hooks/useMobile'
import { HiddenSidebarPill } from '@/routes/layout/app-layout/components/HiddenSidebarPill'
import { PhoneCreateButton, PhoneTabBar } from '@/routes/layout/app-layout/components/PhoneTabBar'
import { useKeyboardInset } from '@/hooks/useKeyboardInset'

const DEFAULT_SIDEBAR_WIDTH = 300
const ICON_ONLY_SIDEBAR_WIDTH = 'calc(var(--sidebar-width-icon) + 1px)'

export const Route = createFileRoute('/_AppWrapper/_AppLayout')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      // In-app sign-in page (there is no hosted UI any more).
      throw redirect({ to: '/auth' })
    }
    // Authenticated but onboarding not complete (no workspace in store) —
    // bounce through `/` so its beforeLoad routes to /callback → /onboarding.
    if (!useLoginStore.getState().workspaceSlug) {
      throw redirect({ to: '/' })
    }
  },
  component: AppLayout,
})

/**
 * Main application layout component that provides the shell for authenticated pages.
 * 
 * This layout wraps all authenticated routes and provides:
 * - Sidebar navigation with dynamic content injection
 * - Authentication guards
 * - Global app context and state management
 * - Responsive design with collapsible sidebar
 * - Toast notifications
 * - Loading states during initialization
 * 
 * Features:
 * - Dynamic sidebar content based on current route
 * - Responsive sidebar with icon-only collapsed state
 * - Context providers for layout state management
 * - Authentication redirection for unauthenticated users
 * - Global loading and error state handling
 * 
 * Route: `/_AppWrapper/_AppLayout` (layout route)
 * 
 * @example
 * ```tsx
 * // This layout wraps all authenticated routes like:
 * // /workspace/tasks, /workspace/projects, etc.
 * // Provides consistent navigation and layout structure
 * ```
 */
export default function AppLayout() {
  const { isAppLoading, hasAppError, setIsDbLoading } = useContext(AppContext)
  const navigate = useNavigate()

  // Auto-switch workspace when URL slug doesn't match current workspace
  const urlSlug = useParams({ strict: false, select: (p) => (p as { workspaceSlug?: string }).workspaceSlug })
  const storeSlug = useLoginStore((s) => s.workspaceSlug)
  const workspaces = useLoginStore((s) => s.workspaces)
  const switchWorkspace = useLoginStore((s) => s.switchWorkspace)

  useEffect(() => {
    if (!urlSlug || !storeSlug || urlSlug === storeSlug) return

    const targetWs = workspaces.find((w) => w.workspaceSlug === urlSlug)
    if (targetWs) {
      // User has access to this workspace — auto-switch
      switchWorkspace(targetWs)
    } else {
      // No access — redirect to current workspace
      void navigate({ to: `/${storeSlug}`, replace: true })
    }
  }, [urlSlug, storeSlug, workspaces, switchWorkspace, navigate])

  /**
   * UI
   */
  const [secSidebar, setSecSidebar] = useState<ReactNode | null>(null)
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH)
  const [headerHeight] = useState(0)
  const [showHeader, setShowHeader] = useState(true)
  const [breadcrumb, setBreadcrumb] = useState<ReactNode | null>(null)
  const [breadcrumbActions, setBreadcrumbActions] = useState<ReactNode | null>(null)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isAskOpen, setIsAskOpen] = useState(false)

  const [askQuestion, setAskQuestion] = useState('')
  // ⌘K's "Create task" (design 29b) opens the global "+" with the query.
  const [createFromSearch, setCreateFromSearch] = useState<{ open: boolean; name: string }>({
    open: false,
    name: '',
  })
  const routeProjectId = useRouteProjectId()

  const openSearch = useCallback(() => setIsSearchOpen(true), [])
  const openCreate = useCallback(() => setCreateFromSearch({ open: true, name: '' }), [])
  useKeyboardInset()
  const openAskWorkspace = useCallback(() => {
    setAskQuestion('')
    setIsAskOpen(true)
  }, [])
  const askFromSearch = useCallback((question: string) => {
    setAskQuestion(question)
    setIsAskOpen(true)
  }, [])
  const createTaskFromSearch = useCallback(
    (name: string) => setCreateFromSearch({ open: true, name }),
    [],
  )

  // G then a letter jumps to a place, as ⌘K's "Go to" rows show (29c).
  const goToPlace = useCallback(
    (shortcut: string) => {
      const place = GO_TO_PLACES.find((p) => p.shortcut === shortcut)
      const slug = useLoginStore.getState().workspaceSlug
      if (place && slug) void navigate({ to: `/${slug}/${place.path}` })
    },
    [navigate],
  )
  useHotkeys('g>i', () => goToPlace('I'))
  useHotkeys('g>t', () => goToPlace('T'))
  useHotkeys('g>u', () => goToPlace('U'))
  useHotkeys('g>n', () => goToPlace('N'))

  // Global Cmd+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsSearchOpen(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Refresh workspace list from API on mount to pick up name/slug changes from other sessions
  useEffect(() => {
    getWorkspaces()
      .then((resp) => useLoginStore.getState().setWorkspaces(resp.data))
      .catch(() => {}) // silently ignore — stale cached list is acceptable as fallback
  }, [])

  // Since migrations are disabled, set DB loading to false immediately
  useEffect(() => {
    setIsDbLoading(false)
  }, [setIsDbLoading])

  // Sidebar mode (design 5a): docked vs rail is chosen per view type and
  // remembered; hidden is a separate global state. Narrow windows never dock.
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const viewType = getSidebarViewType(pathname)
  const sidebarModes = useSidebarModeStore((s) => s.modes)
  const setSidebarMode = useSidebarModeStore((s) => s.setMode)
  const sidebarHidden = useSidebarModeStore((s) => s.hidden)
  const setSidebarHidden = useSidebarModeStore((s) => s.setHidden)
  const isNarrowDesktop = useIsNarrowDesktop()
  const isSettingsSidebar = !!secSidebar && !showHeader
  const sidebarOpen =
    isSettingsSidebar ||
    (!sidebarHidden && !isNarrowDesktop && resolveSidebarMode(sidebarModes, viewType) === 'docked')

  const handleOpenChange = useCallback((open: boolean) => {
    if (isSettingsSidebar) return // Settings keeps its sidebar
    if (sidebarHidden) {
      // Bringing the sidebar back from hidden; `[` / Pin dock it.
      setSidebarHidden(false)
      if (open) setSidebarMode(viewType, 'docked')
      return
    }
    if (isNarrowDesktop) return // Rail + peek-as-drawer only
    setSidebarMode(viewType, open ? 'docked' : 'rail')
  }, [isSettingsSidebar, sidebarHidden, isNarrowDesktop, viewType, setSidebarMode, setSidebarHidden])

  if (isAppLoading && !hasAppError) {
    return (
      <div className="pt-2">
        <Loading />
      </div>
    )
  }

  return (
    <>
      <>
        <AppLayoutContext.Provider
          value={{
            secSidebar,
            setSecSidebar,
            sidebarWidth,
            setSidebarWidth,
            headerHeight,
            showHeader,
            setShowHeader,
            breadcrumb,
            setBreadcrumb,
            breadcrumbActions,
            setBreadcrumbActions,
            openSearch,
            openAskWorkspace,
          }}
        >
          <SidebarProvider
            open={sidebarOpen}
            onOpenChange={handleOpenChange}
            style={
              {
                '--sidebar-width': secSidebar
                  ? `${sidebarWidth}px`
                  : ICON_ONLY_SIDEBAR_WIDTH,
              } as CSSProperties
            }
          >
            <AppSidebar
              secSidebar={secSidebar}
              showHeader={showHeader}
              hidden={sidebarHidden && !isSettingsSidebar}
            />
            <SidebarInset>
              {sidebarHidden && !isSettingsSidebar && <HiddenSidebarPill />}
              <div className="flex flex-1 flex-col min-w-0 min-h-0 overflow-hidden pt-0 from-muted/50 to-background h-full from-30%">
                <TrialBanner className="mx-4 mt-4" dismissible />
                {breadcrumb && (
                  <div className="flex items-center justify-between h-10 px-4 md:px-8 border-b border-border flex-shrink-0 phone:h-auto phone:min-h-14 phone:flex-wrap phone:gap-x-1 phone:pt-[env(safe-area-inset-top)]">
                    <PhoneMenuButton className="mr-1" />
                    <nav className="flex flex-1 items-center gap-1 text-xs text-muted-foreground min-w-0 overflow-hidden">
                      {breadcrumb}
                    </nav>
                    {breadcrumbActions && (
                      <div className="flex items-center gap-0.5 flex-shrink-0 ml-2 phone:contents">
                        {breadcrumbActions}
                      </div>
                    )}
                  </div>
                )}
                <div className="relative flex min-h-0 flex-1 flex-col">
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <TaskRowCountsProvider>
                      <Outlet />
                    </TaskRowCountsProvider>
                  </div>
                  <PhoneCreateButton onCreate={openCreate} />
                </div>
                <PhoneTabBar onSearch={openSearch} />
              </div>
              <Toaster richColors position="bottom-center" mobileOffset={{ bottom: 'calc(var(--safe-area-bottom) + 72px)' }} />
            </SidebarInset>
          </SidebarProvider>
        </AppLayoutContext.Provider>
      </>
      <SearchDialog
        open={isSearchOpen}
        onOpenChange={setIsSearchOpen}
        onCreateTask={createTaskFromSearch}
        onAsk={askFromSearch}
      />
      <QuickCreateTaskDialog
        open={createFromSearch.open}
        onOpenChange={(open) => setCreateFromSearch((current) => ({ ...current, open }))}
        defaultProjectId={routeProjectId}
        initialName={createFromSearch.name}
      />
      <AskWorkspaceDialog
        open={isAskOpen}
        onOpenChange={setIsAskOpen}
        initialQuestion={askQuestion}
      />
      <PWAUpdatePrompt />
    </>
  )
}
